import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { LoanService } from '@treasury/domain/loans';
import { Loan } from '@treasury/domain/loans/loan.entity';
import { TmApiError } from '@treasury/domain/shared';
import { TmContainer } from '@treasury/presentation';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/loan-confirm-step';
import '../partials/loan-create-step';
import '../partials/loan-review-step';

export const tagName = 'loan-workflow-container';
@customElement(tagName)
export class LoanWorkflowContainer extends TmContainer {
    @InjectProperty()
    declare private headerService: HeaderBarService;

    @InjectProperty()
    declare private loanService: LoanService;

    @InjectProperty()
    declare private navService: NavigationService;

    @state()
    private loan = new Loan();

    @state()
    private activeStep = 1;

    @state()
    private isEditLoan = false;

    public connectedCallback(): void {
        super.connectedCallback();
        this.configureHeader();
    }

    public async firstUpdated() {
        const { params } = await this.navService.getRouteData<{
            id: string;
            action: string;
            type: string;
        }>();
        if (params.action === 'edit' && params.id) {
            this.isEditLoan = true;
            this.configureHeader();
            this.loadLoanDetails(params.id);
        }
    }

    protected render() {
        return html`<tm-body class="max-w-2xl h-full mx-auto" .shadow=${false} .padding=${false}>
            ${this.loading ? this.renderBlockingLoader() : this.renderStep()}
        </tm-body>`;
    }

    private configureHeader() {
        switch (this.activeStep) {
            case 1:
                this.headerService.configure({
                    title: this.isEditLoan ? 'Edit Loan Payment' : 'Create Loan Payment',
                    backAction: () => this.navService.navigate('/loans'),
                    enableFdicSign: true,
                });
                break;
            case 2:
                this.headerService.configure({
                    title: 'Review Loan Payment',
                    backAction: () => {
                        this.activeStep = 1;
                        this.configureHeader();
                    },
                });
                break;
            case 3:
                this.headerService.configure({ title: 'Loan Payment Confirmation' });
                break;
            default:
                break;
        }
        this.loading = false;
    }

    private async loadLoanDetails(id: string) {
        this.loading = true;
        try {
            this.loan = await this.loanService.getLoanPaymentDetails(id);
        } catch (error) {
            this.notificationService.renderError(error as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    private async createLoanPayment() {
        this.loading = true;
        try {
            if (this.isEditLoan) {
                const response = await this.loanService.updateLoanPayment(this.loan);
                this.loan = response.transactions;
                this.activeStep = 3;
                this.configureHeader();
                return;
            }
            const response = await this.loanService.createLoanPayment(this.loan);
            [this.loan] = response.transactions;
            this.activeStep = 3;
            this.configureHeader();
        } catch (error) {
            this.loading = false;
            this.notificationService.renderError(error as TmApiError);
        } finally {
            this.loading = false;
        }
    }

    private renderStep() {
        switch (this.activeStep) {
            case 1:
                return html`<loan-create-step
                    .loan=${this.loan}
                    @review=${(e: CustomEvent) => {
                        this.loan = e.detail;
                        this.activeStep = 2;
                        this.configureHeader();
                    }}
                ></loan-create-step>`;
            case 2:
                return html`<loan-review-step
                    .loan=${this.loan}
                    @confirm=${() => {
                        this.createLoanPayment();
                    }}
                ></loan-review-step>`;
            case 3:
                return html`<loan-confirm-step
                    .loan=${this.loan}
                    @start-new-loan=${() => {
                        this.loan = new Loan();
                        this.activeStep = 1;
                        this.configureHeader();
                    }}
                    @view-loans=${() => {
                        this.navService.navigate('/loans');
                    }}
                ></loan-confirm-step>`;
            default:
                return nothing;
        }
    }

    private renderBlockingLoader() {
        if (this.loading) {
            return html`<tm-blocking-loader></tm-blocking-loader>`;
        }
        return nothing;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: LoanWorkflowContainer;
    }
}
